import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { IRoom, IRoomOccupancy } from '../../Interfaces/IRoom';
import { customFetcher } from '../useFetch';

const mapRoomPayload = (room: Partial<IRoom>) => ({
  idRoom: room.idRoom,
  Location_idLocation: room.Location_idLocation,
  Name: room.Name,
  Capacity: room.Capacity ?? null,
});

/** Das generische Backend liefert den Standort-FK relationsbedingt unter dem
 *  Alias `idLocation` (statt der Spalte `Location_idLocation`). Hier auf das im
 *  Frontend durchgaengig genutzte Feld `Location_idLocation` normalisieren,
 *  damit Filter nach Standort (Standort-Dialog, Belegung, Terminformulare)
 *  greifen. */
const normalizeRoom = (room: IRoom & { idLocation?: number }): IRoom => ({
  ...room,
  Location_idLocation: room.Location_idLocation ?? room.idLocation ?? 0,
});

export const useGetRooms = () => {
  const {
    data: rooms,
    isLoading: isLoadingRooms,
    refetch: refetchRooms,
  } = useQuery<IRoom[]>({
    queryKey: ['room'],
    queryFn: async () => {
      const data: (IRoom & { idLocation?: number })[] = await customFetcher({
        endpoint: '/room',
      });
      return data.map(normalizeRoom);
    },
    refetchOnWindowFocus: false,
  });

  return { rooms: rooms ?? [], isLoadingRooms, refetchRooms };
};

/** Belegungsplan eines Standorts im Zeitfenster [dateFrom, dateTo] (YYYY-MM-DD).
 *  Liefert je Raum die zugeordneten Termine. Nur aktiv, wenn ein Standort gewaehlt ist. */
export const useGetRoomOccupancy = (
  idLocation: number | null,
  dateFrom: string,
  dateTo: string
) => {
  const {
    data: occupancy,
    isLoading: isLoadingOccupancy,
    refetch: refetchOccupancy,
  } = useQuery<IRoomOccupancy[]>({
    queryKey: ['roomOccupancy', idLocation, dateFrom, dateTo],
    queryFn: () =>
      customFetcher({
        endpoint: `/location/${idLocation}/roomOccupancy?dateFrom=${dateFrom}&dateTo=${dateTo}`,
      }),
    enabled: idLocation !== null && !!dateFrom && !!dateTo,
    refetchOnWindowFocus: false,
  });

  return {
    occupancy: occupancy ?? [],
    isLoadingOccupancy,
    refetchOccupancy,
  };
};

export const useCreateRoom = () => {
  const queryClient = useQueryClient();
  const { mutateAsync: createRoom, isPending: isPendingCreateRoom } =
    useMutation({
      mutationFn: (room: Omit<IRoom, 'idRoom'>) =>
        customFetcher({
          endpoint: '/room',
          method: 'POST',
          body: mapRoomPayload(room),
        }),
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: ['room'] });
      },
    });

  return { createRoom, isPendingCreateRoom };
};

export const useUpdateRoom = () => {
  const queryClient = useQueryClient();
  const { mutateAsync: updateRoom, isPending: isPendingUpdateRoom } =
    useMutation({
      mutationFn: (room: IRoom) =>
        customFetcher({
          endpoint: '/room',
          method: 'PUT',
          body: mapRoomPayload(room),
        }),
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: ['room'] });
      },
    });

  return { updateRoom, isPendingUpdateRoom };
};

export const useDeleteRoom = () => {
  const queryClient = useQueryClient();
  const { mutateAsync: deleteRoom, isPending: isPendingDeleteRoom } =
    useMutation({
      mutationFn: (idRoom: number) =>
        customFetcher({
          endpoint: `/room/${idRoom}`,
          method: 'DELETE',
        }),
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: ['room'] });
      },
    });

  return { deleteRoom, isPendingDeleteRoom };
};
